import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/auth-fetch";
export function useConversation(deckId?: string) {
  const [state, setState] = useState<{
    deckId?: string;
    mode: "personal" | "shared";
    can_write: boolean;
    user_id?: string;
  }>({ mode: "shared", can_write: false });
  const [tasks, setTasks] = useState<any[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!deckId) return;
    let live = true;
    const refresh = async () => {
      try {
        const [c, t] = await Promise.all([
          apiFetch(`/api/decks/${deckId}/collaboration`),
          apiFetch(`/api/decks/${deckId}/tasks`),
        ]);
        if (!c.ok || !t.ok) throw Error("Could not refresh conversation.");
        const [caps, list] = await Promise.all([c.json(), t.json()]);
        if (live) {
          setState({ ...caps, deckId });
          setTasks(list.tasks);
          setError("");
        }
      } catch (e) {
        if (live) setError((e as Error).message);
      }
    };
    void refresh();
    const timer = setInterval(refresh, 4000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [deckId]);
  return {
    userId: state.user_id,
    mode: state.mode,
    canWrite: state.deckId === deckId && state.can_write,
    ready: !!deckId && state.deckId === deckId,
    tasks: state.deckId === deckId ? tasks : [],
    error,
  };
}
export function ConversationControls({
  deckId,
  conversation,
  intent,
  onIntent,
}: {
  deckId?: string;
  conversation: ReturnType<typeof useConversation>;
  intent: "agent" | "discussion";
  onIntent: (v: "agent" | "discussion") => void;
}) {
  const [error, setError] = useState("");
  const active = conversation.tasks.filter((t) =>
    ["running", "queued", "blocked", "failed", "needs_review"].includes(
      t.status,
    ),
  );
  return (
    <div
      className="pp-conversation-controls"
      style={{
        fontSize: "var(--fs-sm)",
        color: "var(--text-secondary)",
        marginBottom: 12,
      }}
    >
      <label>
        Send to{" "}
        <select
          aria-label="Message destination"
          value={intent}
          onChange={(e) => onIntent(e.target.value as typeof intent)}
          disabled={!conversation.canWrite}
          style={{
            font: "inherit",
            color: "inherit",
            background: "var(--bg-primary)",
            border: "none",
          }}
        >
          <option value="agent">Agent</option>
          <option value="discussion">Team discussion</option>
        </select>
      </label>
      <details style={{ marginTop: 4 }}>
        <summary>Conversation details</summary>
        <p>
          {conversation.mode === "shared"
            ? "Messages and shared files are visible to deck members. Personal Feishu connections stay private."
            : "This is a personal conversation."}
        </p>
        <button
          onClick={async () => {
            const r = await apiFetch(`/api/decks/${deckId}/follow`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ muted: false }),
            });
            setError(
              r.ok
                ? "Following deck updates in Inbox."
                : "Could not follow this deck.",
            );
          }}
        >
          Follow updates
        </button>
        <button
          onClick={async () => {
            const r = await apiFetch(`/api/decks/${deckId}/follow`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ muted: true }),
            });
            setError(
              r.ok
                ? "Deck progress muted. Your task results still appear in Inbox."
                : "Could not mute updates.",
            );
          }}
        >
          Mute updates
        </button>
      </details>
      {conversation.ready && !conversation.canWrite && (
        <span> · Read-only access</span>
      )}
      {active.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary>
            {active.some((t) => t.status === "running")
              ? `Working on ${active.find((t) => t.status === "running")?.author_name || "a member"}’s request`
              : active.some((t) =>
                    ["blocked", "failed", "needs_review"].includes(t.status),
                  )
                ? "Requests need attention"
                : "Requests"}{" "}
            · {active.filter((t) => t.status === "queued").length} queued
          </summary>
          {active.map((t) => (
            <div
              key={t.turn_id}
              style={{
                padding: "8px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              {t.author_name || "Member"} ·{" "}
              {(
                {
                  running: "In progress",
                  queued: "Queued",
                  blocked: "Needs a new request",
                  failed: "Could not finish",
                  needs_review: "Check execution",
                } as Record<string, string>
              )[t.status] || t.status}
              {t.reason && <p>{t.reason}</p>}
              {t.status === "needs_review" && (
                <button
                  onClick={async () => {
                    const r = await apiFetch(
                      `/api/decks/${deckId}/tasks/reconcile`,
                      { method: "POST" },
                    );
                    const data = await r.json();
                    setError(data.message || "Could not verify execution.");
                  }}
                >
                  Check execution
                </button>
              )}
              {t.status === "queued" && t.actor_id === conversation.userId && (
                <button
                  onClick={async () => {
                    try {
                      const r = await apiFetch(
                        `/api/decks/${deckId}/tasks/cancel`,
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ turn_id: t.turn_id }),
                        },
                      );
                      if (!r.ok)
                        throw Error(
                          "Only the sender can cancel a queued request.",
                        );
                      setError("");
                    } catch (e) {
                      setError((e as Error).message);
                    }
                  }}
                >
                  Cancel request
                </button>
              )}
            </div>
          ))}
        </details>
      )}
      {(error || conversation.error) && (
        <p role="status">{error || conversation.error}</p>
      )}
    </div>
  );
}
